import { Injectable } from '@angular/core';
import { HttpEvent } from '@angular/common/http';

import { Actions, ofType, createEffect } from '@ngrx/effects';
import { switchMap, withLatestFrom, tap, map, catchError, take } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { Update } from '@ngrx/entity';
import { combineLatest, of } from 'rxjs';

import * as fromAuthStore from '@apps/auth/store';
import { IAuroraState } from '@apps/aurora.state';
import { FilesService, NotificationService, NotfGravity, NotifType } from '@common/services';
import { Profile } from '@apps/auth/models';
import { ServerListDomainService } from '@apps/bckp/domain';
import { ServerMiscDomainService } from '@apps/bckp/domain/server-misc-domain.service';
import { Server, Image } from '@apps/bckp/domain/models';
import { BckpConstants } from '@apps/bckp/bckp.constants';
import * as fromBckpServersServActions from './serv.actions';
import { getAllServersByIdentifier, getBckpServersServExportQueryString, getBckpServersServQueryString } from '@apps/bckp/store/servers/bckp-servers.selectors';
import { AssetMiscDomainService } from '@apps/cmdb/domain/asset-misc-domain.service';
import { TypeAssetName } from '@apps/cmdb/cmdb.constants';
import { EnrichedAsset, Tag } from '@apps/cmdb/models';
import { Assetify } from '@common/domain/assetify';
import { getAllTags } from '@apps/cmdb/store';
import { joinAssets } from '@common/operators/join-assets';

@Injectable()
export class BckpServersServEffects
{
    constructor (
        private actions: Actions,
        private serverListDomainService: ServerListDomainService,
        private serverMiscDomainSevice: ServerMiscDomainService,
        private store: Store<IAuroraState>,
        private notificationService: NotificationService,
        private filesService: FilesService,
        private assetMiscDomainService: AssetMiscDomainService
    )
    { }

    bckpServersServODataRequested = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBckpServersServActions.BckpServersServODataRequested),
                    withLatestFrom(
                        this.store.select(getBckpServersServQueryString),
                        this.store.select(getAllTags),
                        (_, qs, tags: Tag[]) => ({
                            qs,
                            tags
                        })
                    ),
                    switchMap(({ qs, tags }: { qs: string, tags: Tag[]; }) =>
                        this.serverListDomainService.getLatestImages(qs)
                            .pipe(
                                joinAssets(this.assetMiscDomainService, TypeAssetName.HOST, tags),
                                map((servers: Server[]) =>
                                    fromBckpServersServActions.BckpServersServODataLoaded({ servers })
                                ),
                                catchError(() =>
                                    of(fromBckpServersServActions.BckpServersServODataFailed())
                                )
                            )
                    )
                )
    );

    bckpServersServODataFailed = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBckpServersServActions.BckpServersServODataFailed),
                    tap(() =>
                        this.notificationService.notify(
                            [
                                'BACKUP.SERVERS.NOTF.LIST.TITLE',
                                'BACKUP.SERVERS.NOTF.LIST.MSGFAILED'
                            ],
                            NotfGravity.danger,
                            NotifType.SNACKBAR)
                    )
                ),
        { dispatch: false }
    );

    bckpServersServExportRequested = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBckpServersServActions.BckpServersServExportRequested),
                    withLatestFrom(this.store.select(getBckpServersServExportQueryString)),
                    switchMap(([params, qs]: [any, string]) =>
                        this.serverMiscDomainSevice.export(qs, params.format)
                    ),
                    map((response) =>
                        fromBckpServersServActions.BckpServersServExportSucceeded({ response })
                    )
                )
    );

    bckpServersServExportSucceeded = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBckpServersServActions.BckpServersServExportSucceeded),
                    withLatestFrom(this.store.select(fromAuthStore.getProfile)),
                    tap(([params, profile]: [{response: HttpEvent<Blob>}, Profile]) =>
                    {
                        this.filesService.saveFileFromApi(params.response, profile);
                    })
                ),
        { dispatch: false }
    );
    bckpServersServFilterApplied = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBckpServersServActions.BckpServersServFilterApplied),
                    tap((params: any) =>
                    {
                        this.store.dispatch(fromBckpServersServActions.BckpServersServCleared());
                        this.store.dispatch(fromBckpServersServActions.BckpServersServODataInitialized({
                            scrollSize: BckpConstants.QSServersTop,
                            filter: params.filter
                        }));
                        this.store.dispatch(fromBckpServersServActions.BckpServersServODataRequested());
                    })
                ),
        { dispatch: false }
    );

    bckpServersServSelectedImagesRequested = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBckpServersServActions.BckpServersServSelectedImagesRequested),
                    withLatestFrom(this.store.select(getBckpServersServQueryString)),
                    switchMap(([params, qs]: [any, string]) =>
                        this.serverListDomainService.getImages(`${qs}&terms=serverShort:${params.serverShort}`)
                            .pipe(
                                map((images: Image[]) =>
                                    fromBckpServersServActions.BckpServersServSelectedImagesSucceeded({ images })
                                ),
                                catchError(() =>
                                    of(fromBckpServersServActions.BckpServersServSelectedImagesFailed())
                                )
                            )
                    )
                )
    );

    bckpServersServSelectedImagesFailed = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBckpServersServActions.BckpServersServSelectedImagesFailed),
                    tap(() =>
                        this.notificationService.notify(
                            [
                                'BACKUP.SERVERS.NOTF.DETAILS.TITLE',
                                'BACKUP.SERVERS.NOTF.DETAILS.MSGFAILED'
                            ],
                            NotfGravity.danger,
                            NotifType.SNACKBAR)
                    )
                ),
        { dispatch: false }
    );

    bckpServersServTagAttachRequested = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBckpServersServActions.BckpServersServTagAttachRequested),
                    switchMap((params) =>
                        this.assetMiscDomainService.attachTag(TypeAssetName.HOST, params.identifier, params.name, params.tagId)
                            .pipe(
                                map((enrichedAsset: EnrichedAsset) =>
                                    fromBckpServersServActions.BckpServersServTagAttachExecuted({ enrichedAsset })
                                )
                            )
                    )
                )
    );

    bckpServersServTagAttachExecuted = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBckpServersServActions.BckpServersServTagAttachExecuted),
                    switchMap((params) =>
                        combineLatest([
                            this.store.select(getAllServersByIdentifier(params.enrichedAsset.identifier)),
                            this.store.select(getAllTags)
                        ])
                            .pipe(
                                take(1),
                                tap(([servers, tags]: [Server[], Tag[]]) =>
                                {
                                    servers.forEach((server: Server) =>
                                    {
                                        const updatedServer: Update<Server> = {
                                            id: server.documentId,
                                            changes: {
                                                tagIds: params.enrichedAsset.tagIds,
                                                tagFullNames: Assetify.BuildFullTagNames(params.enrichedAsset.tagIds, tags)
                                            }
                                        };

                                        this.store.dispatch(fromBckpServersServActions.BckpServersServTagAttachSucceeded({ updatedServer: updatedServer }));
                                    });
                                })
                            )
                    )
                ),
        { dispatch: false }
    );

    bckpServersServTagAttachSucceeded = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBckpServersServActions.BckpServersServTagAttachSucceeded),
                    tap(() =>
                        this.notificationService.notify(
                            [
                                'CMDB.ASSETS.ATTACH.TITLE',
                                'CMDB.ASSETS.ATTACH.SUCCEEDED'
                            ],
                            NotfGravity.success,
                            NotifType.SNACKBAR
                        )
                    )
                ),
        { dispatch: false }
    );

    bckpServersServTagDetachRequested = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBckpServersServActions.BckpServersServTagDetachRequested),
                    switchMap((params) =>
                        this.assetMiscDomainService.detachTag(TypeAssetName.HOST, params.identifier, params.tagId)
                            .pipe(
                                map((enrichedAsset: EnrichedAsset) =>
                                    fromBckpServersServActions.BckpServersServTagDetachExecuted({ enrichedAsset })
                                )
                            )
                    )
                )
    );

    bckpServersServTagDetachExecuted = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBckpServersServActions.BckpServersServTagDetachExecuted),
                    switchMap((params) =>
                        combineLatest([
                            this.store.select(getAllServersByIdentifier(params.enrichedAsset.identifier)),
                            this.store.select(getAllTags)
                        ])
                            .pipe(
                                take(1),
                                tap(([servers, tags]: [Server[], Tag[]]) =>
                                {
                                    servers.forEach((server: Server) =>
                                    {
                                        const updatedServer: Update<Server> = {
                                            id: server.documentId,
                                            changes: {
                                                tagIds: params.enrichedAsset.tagIds,
                                                tagFullNames: Assetify.BuildFullTagNames(params.enrichedAsset.tagIds, tags)
                                            }
                                        };

                                        this.store.dispatch(fromBckpServersServActions.BckpServersServTagDetachSucceeded({ updatedServer: updatedServer }));
                                    });
                                })
                            )
                    )
                ),
        { dispatch: false }
    );

    bckpServersServTagDetachSucceeded = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBckpServersServActions.BckpServersServTagDetachSucceeded),
                    tap(() =>
                        this.notificationService.notify(
                            [
                                'CMDB.ASSETS.DETACH.TITLE',
                                'CMDB.ASSETS.DETACH.SUCCEEDED'
                            ],
                            NotfGravity.success,
                            NotifType.SNACKBAR
                        )
                    )
                ),
        { dispatch: false }
    );
}
